import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectSelection.module.css';
import { Project, User, ProjectStatus } from '../../types';
import { Header } from '../../components/layout/Header/Header';
import { LocationIcon, CalendarIcon } from '../../components/common/Icons/Icons';
import { ProjectManagementModal } from '../AdminDashboard/ProjectManagementModal';
import { projectsAPI, companiesAPI } from '../../services/api';

interface ProjectSelectionProps {
  projects: Project[];
  user: User;
  isAdmin: boolean;
  onProjectSelect: (project: Project) => void;
  onLogout: () => void;
}

const getProjectIcon = (projectName: string) => {
  switch (projectName) {
    case 'A1 Amersfoort-Apeldoorn':
      return (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M5 12h14" />
          <path d="M12 5l7 7-7 7" />
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        </svg>
      );
    case 'Kustversterking Petten':
      return (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
          <path d="M21 3l-3 3m0 0l3 3" />
        </svg>
      );
    case 'Sluizen Maasbracht':
      return (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M9 9h6v6H9z" />
          <path d="M12 1v3m0 16v3M1 12h3m16 0h3" />
        </svg>
      );
    default:
      return (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      );
  }
};

const getStatusClassName = (status: ProjectStatus | string) => {
  const statusMap: { [key: string]: string } = {
    'planning': styles.planfase,
    'active': styles.uitvoering,
    'paused': styles.voorbereiding,
    'completed': styles.uitvoeringStabiel,
    'cancelled': styles.uitvoeringKritiek,
    [ProjectStatus.UitvoeringKritiek]: styles.uitvoeringKritiek,
    [ProjectStatus.Voorbereiding]: styles.voorbereiding,
    [ProjectStatus.Uitvoering]: styles.uitvoering,
    [ProjectStatus.Planfase]: styles.planfase,
    [ProjectStatus.UitvoeringUrgent]: styles.uitvoeringUrgent,
    [ProjectStatus.UitvoeringStabiel]: styles.uitvoeringStabiel,
  };

  return statusMap[status] || '';
};

const getStatusLabel = (status: string) => {
  const labelMap: { [key: string]: string } = {
    'planning': 'Planning',
    'active': 'Actief',
    'paused': 'Gepauzeerd',
    'completed': 'Afgerond',
    'cancelled': 'Geannuleerd',
  };
  return labelMap[status] || status;
};

export const ProjectSelection: React.FC<ProjectSelectionProps> = ({
  projects,
  user,
  isAdmin,
  onProjectSelect,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);

  useEffect(() => {
    if (isAdmin) {
      companiesAPI.getAll().then(setCompanies).catch(() => {});
    }
  }, [isAdmin]);

  const handleProjectClick = (project: Project) => {
    navigate(`/project/${project.id}/dashboard`);
  };

  const handleEditProject = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    setEditingProject(project);
    setShowProjectModal(true);
  };

  const handleSaveProject = async (projectData: any) => {
    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.id, projectData);
      } else {
        await projectsAPI.create(projectData);
      }
      setShowProjectModal(false);
      setEditingProject(null);
      window.location.reload();
    } catch (error) {
      console.error('Failed to save project:', error);
    }
  };

  return (
    <div className={styles.projectSelection}>
      <Header user={user} showProjectSelector={false} onLogout={onLogout} />

      <div className={styles.projectContent}>
        <div className={styles.pageHeader}>
          <h1 className={styles.projectTitle}>Mijn projecten</h1>

          {isAdmin && (
            <div className={styles.adminActions}>
              <button
                className={styles.adminButton}
                onClick={() => navigate('/admin')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                Admin beheer
              </button>
              <button
                className={styles.newProjectButton}
                onClick={() => { setEditingProject(null); setShowProjectModal(true); }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Nieuw project
              </button>
            </div>
          )}
        </div>

        {projects.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            <h2>Geen projecten beschikbaar</h2>
            <p>
              {isAdmin
                ? 'Je bent nog niet toegewezen aan projecten. Maak een nieuw project aan of wijs jezelf toe via Admin beheer.'
                : 'Je bent nog niet toegewezen aan een project. Neem contact op met je beheerder.'}
            </p>
            {isAdmin && (
              <button
                className={styles.newProjectButton}
                onClick={() => { setEditingProject(null); setShowProjectModal(true); }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Nieuw project aanmaken
              </button>
            )}
          </div>
        ) : (
          <div className={styles.projectGrid}>
            {projects.map((project) => (
              <div
                key={project.id}
                className={styles.projectCard}
                onClick={() => handleProjectClick(project)}
              >
                <div
                  className={styles.projectImage}
                  style={{
                    backgroundImage: project.headerImage
                      ? `url(${project.headerImage})`
                      : `url(/assets/images/${project.image})`,
                  }}
                >
                  {(project as any).company?.logo && (
                    <img
                      src={(project as any).company.logo}
                      alt={(project as any).company.name || 'Klant'}
                      className={styles.projectLogo}
                    />
                  )}
                  {isAdmin && (
                    <button
                      className={styles.editProjectBtn}
                      onClick={(e) => handleEditProject(e, project)}
                      title="Project bewerken"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className={styles.projectInfo}>
                  <h3 className={styles.projectName}>{project.fullName || project.name}</h3>

                  <div className={styles.projectDetails}>
                    <div className={styles.projectDetail}>
                      <LocationIcon />
                      {project.location}
                    </div>
                    <div className={styles.projectDetail}>
                      <CalendarIcon />
                      {project.period || `${project.startDate ? new Date(project.startDate).getFullYear() : ''}`}
                    </div>
                  </div>

                  <span className={`${styles.projectStatus} ${getStatusClassName(project.status)}`}>
                    {project.statusLabel || getStatusLabel(project.status as string)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showProjectModal && (
        <ProjectManagementModal
          project={editingProject}
          companies={companies}
          onSave={handleSaveProject}
          onClose={() => { setShowProjectModal(false); setEditingProject(null); }}
        />
      )}
    </div>
  );
};
